import { get } from '../../helpers/api';

const boundsParams = (bbox) => {
  const fields = {
    swLng: bbox.swLng,
    swLat: bbox.swLat,
    neLng: bbox.neLng,
    neLat: bbox.neLat,
  };
  const invalidKeys = Object.entries(fields)
    .filter(([, value]) => !Number.isFinite(value))
    .map(([key]) => key);
  if (invalidKeys.length > 0) {
    throw new Error(`Invalid bbox: ${invalidKeys.join(', ')} must be finite number(s)`);
  }
  return fields;
};

/** Live eligibility limits, each with a `source` describing where it came from. */
const fetchLimits = async () => {
  const { data } = await get('/admin/lane-map/limits');
  return data;
};

/**
 * Lanes scoped to a city or a viewport. One of `cityId` or `bbox` is required —
 * the server rejects an unscoped query rather than returning thousands of lanes.
 */
const fetchLanes = async ({ cityId, bbox, zoom } = {}) => {
  const params = new URLSearchParams();
  if (cityId) params.append('cityId', cityId);
  if (bbox) {
    Object.entries(boundsParams(bbox)).forEach(([key, value]) => params.append(key, value));
  }
  if (Number.isFinite(zoom)) {
    const clampedZoom = Math.min(22, Math.max(1, Math.round(zoom)));
    params.append('zoom', clampedZoom);
  }
  const { data } = await get(`/admin/lane-map/lanes?${params.toString()}`);
  return data;
};

/** Every station in the viewport, bookable or not. */
const fetchStations = async (bbox) => {
  const params = new URLSearchParams(boundsParams(bbox));
  const { data } = await get(`/admin/lane-map/stations?${params.toString()}`);
  return data;
};

/**
 * Stations near one lane, each carrying precomputed PostGIS distances so the UI
 * can apply every threshold client-side without another round trip.
 */
const fetchLaneStations = async ({ laneId, maxRadiusMeters }) => {
  const params = new URLSearchParams();
  if (maxRadiusMeters) params.append('maxRadiusMeters', maxRadiusMeters);
  const { data } = await get(`/admin/lane-map/lanes/${laneId}/stations?${params.toString()}`);
  return data;
};

/** Exact PostGIS buffer geometry around a lane, for the shaded corridor bands. */
const fetchLaneBuffer = async ({ laneId, radiusMeters }) => {
  const params = new URLSearchParams({ radiusMeters });
  const { data } = await get(`/admin/lane-map/lanes/${laneId}/buffer?${params.toString()}`);
  return data;
};

export default {
  fetchLimits,
  fetchLanes,
  fetchStations,
  fetchLaneStations,
  fetchLaneBuffer,
};
